import { queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import type {
  APINrgkickFeat,
  APIParkUser,
  APIParkUserDetail,
  APIUserGroupSum,
  APIUserGroupSumFilters,
} from '@/types/index.ts';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * The current admin's users (`GET parkadmin/current/users/?is_readonly=`). This
 * is **account/admin-level data, independent of the selected park** — the endpoint
 * takes no park id and the backend does not scope it to a park (unlike `coupons/`,
 * whose park-coupling is an invisible backend dependency). Keyed only by the active
 * flag, so it persists across park switches. Active users are sorted by username;
 * expired keep server order (matches Vue).
 *
 * The per-user rolling-30-day consumption is a **separate** query
 * ([usersConsumptionQueryOptions]) so the list renders without waiting on the
 * aggregate, and so user mutations (which invalidate `['users']`) don't refetch it.
 */
export function usersQueryOptions(isActive: boolean) {
  return queryOptions({
    queryKey: ['users', isActive ? 'active' : 'expired'],
    queryFn: async () => {
      const { data: users } = await api.get<Array<APIParkUser>>(
        `parkadmin/current/users/?is_readonly=${!isActive}`,
      );
      return isActive ?
          [...users].sort((a, b) =>
            a.username.toLowerCase().localeCompare(b.username.toLowerCase()),
          )
        : users;
    },
  });
}

/**
 * Rolling 30-day consumption (kWh) for a set of users, in one batched request
 * (`POST parkadmin/current/charges/user_group_sum/`) — the grouped counterpart to
 * the old per-user `charges/sum/` fan-out. Returns a `user_id → kWh` map; the
 * backend zero-fills every requested id, so a present id is always found
 * (missing ⇒ treat as 0 defensively).
 *
 * Sent as a POST body rather than a query string because this call scales with the
 * admin's whole user list, and a `user_id__in` query string overflowed nginx's
 * request-line limit past ~1200 ids. The endpoint still serves the identical GET
 * form; only the transport differs.
 *
 * Keyed by the **sorted id set** (and deliberately OUTSIDE the `['users']` prefix):
 * changing the set of users — create / expire — mints a new key and refetches with
 * the correct ids automatically, while field-only edits (email / password / NRGKick),
 * which invalidate `['users']` but leave the id set unchanged, never touch it.
 */
export function usersConsumptionQueryOptions(isActive: boolean, userIds: Array<number>) {
  const sortedIds = [...userIds].sort((a, b) => a - b);
  return queryOptions({
    queryKey: ['users-consumption', isActive ? 'active' : 'expired', sortedIds],
    enabled: sortedIds.length > 0,
    queryFn: async (): Promise<Map<number, number>> => {
      const filters: APIUserGroupSumFilters = {
        user_id__in: sortedIds,
        start__gte: new Date(Date.now() - THIRTY_DAYS_MS).toISOString(),
      };
      const { data } = await api.post<Array<APIUserGroupSum>>(
        'parkadmin/current/charges/user_group_sum/',
        filters,
      );
      return new Map(data.map((row) => [row.user_id, row.energy_wh / 1000]));
    },
  });
}

/**
 * A single park user (`GET parkadmin/current/users/{id}/`). Loader-primed by the
 * `/users/$id` layout route and read with `useSuspenseQuery` on the hub; also
 * subscribed by the header breadcrumb and the document title for the dynamic
 * username. Keyed `['user', id]` — the same prefix as the per-user NRGKick query
 * below, and deliberately outside the `['users']` list prefix (list-invalidating
 * mutations don't refetch it; navigation remounts it fresh anyway).
 */
export function userQueryOptions(userId: number) {
  return queryOptions({
    queryKey: ['user', userId],
    queryFn: async () => {
      const { data } = await api.get<APIParkUserDetail>(`parkadmin/current/users/${userId}/`);
      return data;
    },
  });
}

/**
 * NRGKick feature config for a single user
 * (`GET parkadmin/current/users/{id}/nrgkick/`). Resolves `null` when NRGKick is
 * not enabled (the backend returns an empty body). Shared by the user-info and
 * NRGKick-edit dialogs.
 */
export function userNrgkickQueryOptions(userId: number) {
  return queryOptions({
    queryKey: ['user', userId, 'nrgkick'],
    queryFn: async (): Promise<APINrgkickFeat | null> => {
      const { data } = await api.get<APINrgkickFeat | '' | null>(
        `parkadmin/current/users/${userId}/nrgkick/`,
      );
      return data || null;
    },
  });
}
